import { cn } from '@/helpers/classnames';
import Input from '../core/Input';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldError,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { ReactNode, useMemo } from 'react';

interface InputTextFieldProps<
  T extends FieldValues,
> extends UseControllerProps<T> {
  type?: string;
  label?: string;
  editMode?: boolean;
  placeholder?: string;
  noValueDescription?: ReactNode;
  error?: FieldError;
  helperText?: string;
  helperTextReserveSpace?: boolean;
  onBlur?: () => void;
  className?: string;
  required?: boolean;
  maxLength?: number;
  normalize?: (value: string) => string;
}

export const InputText = <T extends FieldValues>({
  type,
  label,
  editMode,
  placeholder,
  noValueDescription,
  error,
  helperText,
  helperTextReserveSpace = false,
  onBlur,
  className,
  required,
  maxLength: maxLengthProp,
  normalize,
  ...props
}: InputTextFieldProps<T>) => {
  const { field } = useController(props);
  const { formatMessage } = useIntl();
  const inputType = type === 'multiline' ? 'multiline' : 'text';
  const maxLength = maxLengthProp ?? (type === 'multiline' ? 2000 : 100);
  const inputMode = type === 'number' ? 'numeric' : undefined;
  const pattern = type === 'number' ? '[0-9]*' : undefined;

  const errorMessage = useMemo(() => {
    if (error?.message) {
      return error?.message;
    }
    if (typeof field.value === 'string' && field.value.length === maxLength) {
      return formatMessage(
        { defaultMessage: 'Maximalt {length} tecken' },
        { length: maxLength },
      );
    }
    return undefined;
  }, [error, field.value, formatMessage, maxLength]);

  return editMode === true ? (
    <Input
      id={field.name}
      type={inputType}
      value={field.value ?? ''}
      placeholder={placeholder}
      className={className}
      label={label}
      onChange={(e) => {
        const nextValue = normalize
          ? normalize(e.target.value)
          : e.target.value;
        if (nextValue !== e.target.value) {
          e.target.value = nextValue;
        }
        field.onChange(nextValue);
      }}
      error={errorMessage}
      onBlur={() => {
        field.onBlur();
        onBlur?.();
      }}
      helperText={helperText}
      helperTextReserveSpace={helperTextReserveSpace}
      maxLength={maxLength}
      inputMode={inputMode}
      pattern={pattern}
      required={required}
    />
  ) : (
    <span
      className={cn(
        'break-all',
        !field.value && 'italic text-text-700',
        className,
      )}
    >
      {(field.value || noValueDescription) ??
        formatMessage({ defaultMessage: 'Inget värde' })}
    </span>
  );
};
